import React, { useEffect, useState } from 'react';
import { CheckCircle2, XCircle, Info, X } from 'lucide-react';

// =============================================================================
// Toast — the app-wide notification surface (Sep 2026).
//
// Replaces the 13 native `alert()` calls and gives the silent `console.error`
// paths a user-visible outlet. Deliberately NOT a context/provider: a plain
// module-level bus means non-component code (hooks/useRoster.js, one-off
// handlers) can fire a toast with a bare import — no prop threading, no
// provider wrapping, no invasive changes to the component tree.
//
//   import { toast } from '../components/Toast';
//   toast.success("Roster published.");
//   toast.error("Upload failed.");
//   toast.info("Nothing to do.");
//
// <ToastHost/> is mounted once in App.js and renders the stack top-right.
// Success/info auto-dismiss in 4s, errors stay 8s; every toast has a manual ✕.
// =============================================================================

let listeners = [];
let idSeq = 0;

const emit = (type, message) => {
  const t = { id: ++idSeq, type, message: String(message || '') };
  if (listeners.length === 0) {
    // Host not mounted (very early init) — don't lose the signal entirely.
    (type === 'error' ? console.error : console.log)(`[toast:${type}]`, t.message);
    return;
  }
  listeners.forEach((l) => l(t));
};

export const toast = {
  success: (m) => emit('success', m),
  error: (m) => emit('error', m),
  info: (m) => emit('info', m),
};

const STYLES = {
  success: { border: 'border-l-emerald-500', icon: CheckCircle2, iconColor: 'text-emerald-500' },
  error: { border: 'border-l-rose-500', icon: XCircle, iconColor: 'text-rose-500' },
  info: { border: 'border-l-teal-600', icon: Info, iconColor: 'text-teal-600' },
};

export const ToastHost = () => {
  const [toasts, setToasts] = useState([]);

  useEffect(() => {
    const timers = new Map();
    const onToast = (t) => {
      setToasts((prev) => [...prev, t]);
      const ttl = t.type === 'error' ? 8000 : 4000;
      timers.set(t.id, setTimeout(() => {
        setToasts((prev) => prev.filter((x) => x.id !== t.id));
        timers.delete(t.id);
      }, ttl));
    };
    listeners.push(onToast);
    return () => {
      listeners = listeners.filter((l) => l !== onToast);
      timers.forEach((h) => clearTimeout(h));
    };
  }, []);

  const dismiss = (id) => setToasts((prev) => prev.filter((x) => x.id !== id));

  if (toasts.length === 0) return null;

  return (
    <div
      className="fixed top-4 right-4 z-[100] flex flex-col gap-2 w-80 max-w-[calc(100vw-2rem)]"
      role="status"
      aria-live="polite"
    >
      {toasts.map((t) => {
        const s = STYLES[t.type] || STYLES.info;
        const Icon = s.icon;
        return (
          <div
            key={t.id}
            className={`flex items-start gap-3 bg-white border border-slate-200 border-l-4 ${s.border} rounded-xl shadow-lg px-4 py-3 animate-[toastIn_.18s_ease-out]`}
          >
            <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${s.iconColor}`} />
            <p className="flex-1 text-xs font-medium text-slate-700 leading-relaxed break-words">
              {t.message}
            </p>
            <button
              onClick={() => dismiss(t.id)}
              aria-label="Dismiss notification"
              className="flex-shrink-0 p-0.5 rounded text-slate-400 hover:text-slate-600"
              style={{ background: 'transparent', boxShadow: 'none' }}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        );
      })}
      <style>{`@keyframes toastIn { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: none; } }`}</style>
    </div>
  );
};
